import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState, useEffect, useMemo } from "react";
import ACModalV2 from "ac-components/ac-modal/ACModalV2";
import { Typography } from "antd";
import './AnyaSummary.less'; // Import the CSS file
import AnyaHistoryMenuIcon from "ac-components/ac-icons/AnyaHistoryMenuIcon";
import AnyaSidebarHistory from "./AnyaSideBarHistory";
import {
    deleteAnyaConversationMessages,
    getAnyaConversations,
} from "restAPI/AnyaAPI";
import message from "components/CustomMessage";
import { sha256Hash } from "./anyaUtils";
import ACButtonGroup, { ACButtonGroupItem } from "ac-components/ACButtonGroup";
import PlusIcon from "ac-components/ac-icons/PlusIcon";
import EllipsisVerticalIcon from "ac-components/ac-icons/EllipsisVerticalIcon";
import ACDropdown from "ac-components/ACDropdown";
import TrashIcon from "ac-components/ac-icons/TrashIcon";
import Text from "antd/lib/typography/Text";
import AnyaConversation from "./AnyaConversation";
import { useHistory } from "react-router-dom";

const AnyaModal = forwardRef(function AnyaModal({ props,showBorder = true }, ref) {
    const thisRef = useRef();
    const history = useHistory();
    const [messages, setMessages] = useState([]);
    const [isGeneratingResponse, setIsGeneratingResponse] = useState(true);
    const [title, setTitle] = useState(props?.config?.title)
    const [chatHistory, setChatHistory] = useState(null);
    const [showHistory,setShowHistory] = useState(false);
    const [chatId, setChatId] = useState(null);
    const [isNewChat,setIsNewChat] = useState(props?.isNewChat || false);
    const [personaId, setPersonaId] = useState(null);
    const modalSettings = ACModalV2.useModalSettings();
    const [startWithEmptyPrompt,setStartWithEmptyPrompt] = useState(props?.startWithEmptyPrompt || false);
    const widgetType = props?.config?.widgetType === undefined ? "Findings" : props?.config?.widgetType
    const idFromURL = useMemo(() => {
        const url = new URL(window.location.href);
        const pathName = url.href.split('?')[0];
        const urlParts = pathName.split('/');
        const dashboardId = urlParts[urlParts.length - 1];
        return dashboardId
    }, []);
    const dashboardId = props?.dashboardId || idFromURL
    const invocationContext = props?.invocationContext

    // Close modal on route change (e.g., when user clicks on widget redirect)
    useEffect(() => {
        const unlisten = history.listen(() => {
            modalSettings.close();
        });
        return () => unlisten();
    }, [history, modalSettings]); 
        
    const fetchChatId = useCallback(async (chatHistoryParam) => {
        const normalizedTitle = title || "";
        const hashInput = dashboardId + normalizedTitle + widgetType;
        const currentWidgetId = await sha256Hash(hashInput);
        const conversation = chatHistoryParam?.filter((convo) => convo.widgetId === currentWidgetId);
        if (conversation.length > 0) {
            setChatId(conversation[0].conversationId);
        } else {
            setIsNewChat(true);
        }
    }, [dashboardId, title, widgetType]);

    const fetchChatHistory = useCallback(async (shouldUpdateChatId = true) => {
        try {
            // Check if existingConversations are provided from props (e.g., from RemediationAction)
            const existingConversations = props?.config?.existingConversations;
            if (existingConversations && existingConversations.length > 0) {
                // Use existing conversations directly, skip list API call
                setChatHistory(existingConversations);
                
                if (shouldUpdateChatId) {
                    fetchChatId(existingConversations);
                }
                return;
            }
            
            // Exclude finding remediations for sidebar and widget calls, but not for finding remediation contexts
            const apiParams = !existingConversations ? { excludeFindingRemediations: true } : {};
            
            const response = await getAnyaConversations(apiParams).promise;
            const newHistory = response.data?.history || [];
            setChatHistory(newHistory);

            if (shouldUpdateChatId) {
                fetchChatId(newHistory);
            }
        } catch {
            message.error("Failed to fetch conversations");
        } 
    }, [fetchChatId, props?.config?.existingConversations]);

    const shouldAutoSelectChat = !!(props?.config?.title || props?.dashboardId);

    useEffect(() => {
        // When opening Anya from sidebar, start with fresh state (no chat selected), only auto-select previous chat if we have specific context (like from a widget)
        fetchChatHistory(shouldAutoSelectChat);
    }, [fetchChatHistory, shouldAutoSelectChat]);

    const switchConversation = useCallback((conversationId) => {
        if (chatId !== conversationId) {
            setIsNewChat(false)
            setChatId(conversationId)
            setMessages([]) // Clear messages when switching conversations
            setIsGeneratingResponse(true) // Show loading state while fetching new messages
        }
    }, [chatId])


    useImperativeHandle(ref, () => ({
        ...thisRef.current,
    }), []);

    const messageContainerRef = useRef(null);

    const items = [
        {
          label: <div className="d-flex align-items-center  danger"><TrashIcon className="font-size-24 m-r-xs" /><Text className="danger">Delete</Text></div>,
          value: "delete",
          disabled: chatId===null
        },
    ]

    const createNewChat = useCallback(() => {
        setChatId(null);
        setIsNewChat(true);
        setTitle(undefined);
        setMessages([]);
        setStartWithEmptyPrompt(true);
    },[]); 

    const onDropDownOptionClick = (value) => {
        switch (value) {
            case "delete":
                onDelete();
                break;
            default:
                break;
        }
    };

const onDelete = useCallback(
    async () => {
      const _onDelete = async () => {
        try {
          await deleteAnyaConversationMessages(chatId).promise;
          fetchChatHistory(false);
          message.success("Conversation Deleted");
        
            createNewChat();
        } catch (e) {
          console.log(e);
          message.error("Error while deleting conversation, Please try again.");
        }
      };
      _onDelete(chatId);
      return;
    },
    [chatId, createNewChat, fetchChatHistory]); 
    
    
    const toggleHistory = () => {
        if(showHistory){
            setShowHistory(false);
            modalSettings.updateWidth(prevWidth => prevWidth - 340);
        } else {
            setShowHistory(true);
            modalSettings.updateWidth(prevWidth => prevWidth + 340);
        }
    }

    return (
        <div className="anya-prose">
            {showHistory && <div className="anya-history-container">
                <AnyaSidebarHistory fetchChatHistory={fetchChatHistory} chatHistory={chatHistory} chatId={chatId} onHistorySelected={switchConversation} createNewChat={createNewChat}/>
            </div>}
            <div className="flex-1 anya-popup-summary" style={!showBorder ? { borderLeft: 'none' } : {}}>
            {(props?.showTitle===true || props?.showTitle===undefined )&& <ACModalV2.Title>
                <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                    <AnyaHistoryMenuIcon onClick={toggleHistory} className="m-r-xs clickable" />
                    <Typography.Text>Anya Insights</Typography.Text>
                </div>
                <ACButtonGroup size="md">
                    <ACDropdown items={items} onOptionClick={onDropDownOptionClick}>
                        <ACButtonGroupItem
                            id="more-actions"
                        >
                            <EllipsisVerticalIcon />
                        </ACButtonGroupItem>
                    </ACDropdown>
                    <ACButtonGroupItem onClick={createNewChat}>
                        <PlusIcon />
                    </ACButtonGroupItem>
                </ACButtonGroup>
                </div>
            </ACModalV2.Title>}
            <AnyaConversation
            chatId={chatId}
            isNewChat={isNewChat}
            title={title}
            widgetType={widgetType}
            dashboardId={dashboardId}
            messages={messages}
            setMessages={setMessages}
            isGeneratingResponse={isGeneratingResponse}
            setIsGeneratingResponse={setIsGeneratingResponse}
            personaId={personaId}
            setPersonaId={setPersonaId}
            fetchChatHistory={fetchChatHistory}
            setChatId={setChatId}
            messageContainerRef={messageContainerRef}
            defaultPromptPrefix={props?.defaultPromptPrefix}
            showSuggestedQuestions={props?.showSuggestedQuestions}
            startWithEmptyPrompt={startWithEmptyPrompt}
            invocationContext={invocationContext}
            />  
            </div>
        </div>
    )
});

export default AnyaModal;

export const anyaPopupCommonProps = {
    defaultPlacement: "right", 
    width: 799,
    mask: true,
    fullHeight: true,
}
